import type { ControlPoint, Coordinate, Drawing } from '@/types'
import { distance, distanceWithinThreshold } from './Geometry.utils'
import {
	getDrawingEndPoint,
	getDrawingStartPoint,
} from './DrawingPoint.utils'

export interface SnapTarget {
	drawingId: string
	pointId: string
	point: ControlPoint
	distance: number
}

export interface PlayerForSnap {
	id: string
	x: number
	y: number
	label: string
	color: string
}

export interface PlayerSnapTarget {
	playerId: string
	point: Coordinate
	distance: number
}

export interface BestPlayerSnapTarget {
	playerId: string
	pointId: string
	playerPosition: Coordinate
	distance: number
}

export interface DrawingSnapTarget {
	drawingId: string
	pointId: string
	point: Coordinate
	distance: number
}

export interface SnapTargetResult {
	playerSnapTarget: PlayerSnapTarget | null
	drawingSnapTarget: SnapTarget | null
}

export interface SnapSearchOptions {
	position: Coordinate
	players?: PlayerForSnap[]
	drawings: Drawing[]
	excludeDrawingId: string
	playerRadiusFeet: number
	drawingThresholdFeet: number
}

export function findSnapTarget(
	position: Coordinate,
	drawings: Drawing[],
	excludeDrawingId: string,
	threshold: number,
): SnapTarget | null {
	let closest: SnapTarget | null = null

	for (const drawing of drawings) {
		if (drawing.id == excludeDrawingId) continue

		const startPoint = getDrawingStartPoint(drawing)
		const endPoint = getDrawingEndPoint(drawing)
		const candidates = [startPoint, endPoint].filter(
			(point): point is ControlPoint => Boolean(point),
		)

		for (const point of candidates) {
			const distance = distanceWithinThreshold(position, point, threshold)
			if (distance === null) continue

			if (!closest || distance < closest.distance) {
				closest = {
					drawingId: drawing.id,
					pointId: point.id,
					point,
					distance,
				}
			}
		}
	}

	return closest
}

export function findPlayerSnapTarget(
	position: Coordinate,
	players: PlayerForSnap[],
	threshold: number,
): PlayerSnapTarget | null {
	let closest: PlayerSnapTarget | null = null

	for (const player of players) {
		const playerPoint = { x: player.x, y: player.y }
		const distance = distanceWithinThreshold(position, playerPoint, threshold)
		if (distance === null) continue

		if (!closest || distance < closest.distance) {
			closest = {
				playerId: player.id,
				point: playerPoint,
				distance,
			}
		}
	}

	return closest
}

/**
 * Finds the best player snap target by checking both start and end points of a drawing.
 * Returns the closest snap target (prioritizing end point if distances are equal).
 */
export function findBestPlayerSnapTarget(
	drawing: Drawing,
	players: PlayerForSnap[],
	threshold: number,
): BestPlayerSnapTarget | null {
	const startPoint = getDrawingStartPoint(drawing)
	const endPoint = getDrawingEndPoint(drawing)

	let bestTarget: BestPlayerSnapTarget | null = null

	if (startPoint) {
		const snap = findPlayerSnapTarget(
			{ x: startPoint.x, y: startPoint.y },
			players,
			threshold,
		)
		if (snap) {
			bestTarget = {
				playerId: snap.playerId,
				pointId: startPoint.id,
				playerPosition: snap.point,
				distance: snap.distance,
			}
		}
	}

	if (endPoint && endPoint.id !== startPoint?.id) {
		const snap = findPlayerSnapTarget(
			{ x: endPoint.x, y: endPoint.y },
			players,
			threshold,
		)
		if (snap && (!bestTarget || snap.distance < bestTarget.distance)) {
			bestTarget = {
				playerId: snap.playerId,
				pointId: endPoint.id,
				playerPosition: snap.point,
				distance: snap.distance,
			}
		}
	}

	return bestTarget
}

export function findDrawingSnapTarget(
	position: Coordinate,
	drawings: Drawing[],
	threshold: number,
): DrawingSnapTarget | null {
	let closest: DrawingSnapTarget | null = null

	for (const drawing of drawings) {
		// Skip drawings that already have a player linked
		if (drawing.playerId) continue

		// Check start and end points only (not intermediate points)
		const startPoint = getDrawingStartPoint(drawing)
		const endPoint = getDrawingEndPoint(drawing)
		const candidates = [startPoint, endPoint].filter(
			(point): point is ControlPoint => Boolean(point),
		)

		for (const point of candidates) {
			const distance = distanceWithinThreshold(position, point, threshold)
			if (distance === null) continue

			if (!closest || distance < closest.distance) {
				closest = {
					drawingId: drawing.id,
					pointId: point.id,
					point: { x: point.x, y: point.y },
					distance,
				}
			}
		}
	}

	return closest
}

/**
 * Unified snap target detection: checks player snapping first (higher priority),
 * then drawing snapping. Returns both targets or null if no snap found.
 * Used to eliminate duplication in control point overlays.
 */
export function findAllSnapTargets(
	options: SnapSearchOptions,
): SnapTargetResult {
	const {
		position,
		players,
		drawings,
		excludeDrawingId,
		playerRadiusFeet,
		drawingThresholdFeet,
	} = options

	// Check player snap first (higher priority)
	if (players && players.length > 0) {
		const playerTarget = findPlayerSnapTarget(
			position,
			players,
			playerRadiusFeet,
		)
		if (playerTarget) {
			return {
				playerSnapTarget: playerTarget,
				drawingSnapTarget: null,
			}
		}
	}

	// No player snap found, check drawing snap
	const drawingTarget = findSnapTarget(
		position,
		drawings,
		excludeDrawingId,
		drawingThresholdFeet,
	)

	return {
		playerSnapTarget: null,
		drawingSnapTarget: drawingTarget,
	}
}

export function calculateUnlinkPosition(
	playerPos: Coordinate,
	secondToLastPoint: Coordinate,
	unlinkDistance: number,
): Coordinate {
	const dx = secondToLastPoint.x - playerPos.x
	const dy = secondToLastPoint.y - playerPos.y
	const length = distance(secondToLastPoint, playerPos)

	if (length == 0) {
		return { x: playerPos.x, y: playerPos.y - unlinkDistance }
	}

	const unitX = dx / length
	const unitY = dy / length

	return {
		x: playerPos.x + unitX * unlinkDistance,
		y: playerPos.y + unitY * unlinkDistance,
	}
}


